import { AlarmClock, Ban, Hourglass, type LucideIcon, PhoneOff, PhoneOutgoing } from "lucide-react";
import { dispositionKindText, formatSpan, type Term } from "@/lib/campaign";
import type { DispositionInput, DispositionUpdateInput } from "@/lib/queries";
import { RECOVERY_STAGE } from "@/lib/stages";
import type { Disposition, Role, Tenant } from "@/lib/types";

/**
 * The dispositions catalog as the console shows and edits it (ADR-017): what an outcome does to the
 * lead, in words, and the arithmetic between the catalog's form and `POST`/`PUT /dispositions`.
 *
 * The vocabularies mirror `DISPOSITION_KINDS` and `DISPOSITION_CATEGORIES` in `platform`'s
 * `models/dispositions.py`, and the bounds `DispositionCreate` in core-api's `schemas.py`. What
 * happens to the lead is `after_disposition` in the dialer's `recycling.py`, the one place a kind, a
 * backoff and a hold mean anything. A kind's label is `dispositionKindText` in `lib/campaign.ts`,
 * which a campaign's Dispositions tab already shows. The server decides all of it; the copies here
 * say what it will do before a save does. Pure, for `test/dispositions.test.ts`.
 */

/** `_CAMPAIGN_SLUG_RE`: what `disposition.set`, the CRM's tags and the reports carry. */
export const DISPOSITION_SLUG_RE = /^[a-z0-9][a-z0-9_-]{0,62}$/;
export const LABEL_MAX = 80;

export const DISPOSITION_KINDS = ["terminal", "callback", "retry", "hold"] as const;
export const DISPOSITION_CATEGORIES = ["contact", "no_contact", "success", "compliance"] as const;

const KIND: Record<string, string> = {
  terminal:
    "The cadence the lead is on ends. If the outcome puts the lead in a stage a campaign has a cadence for, that cadence calls them.",
  callback:
    "The dialer calls the lead back after the backoff, for the rep who took this call, without spending an attempt.",
  retry:
    "One of the campaign's attempts is spent, and the dialer tries again after the backoff until they run out.",
  hold:
    "The lead is kept out of the lists until the hold has passed, as while an appointment or a contract review is pending.",
};

/** A kind in plain words. */
export function kindTerm(kind: string): Term {
  return {
    label: dispositionKindText(kind),
    description: KIND[kind] ?? "Not a kind this console knows. The dialer treats it as a retry.",
  };
}

const CATEGORY: Record<string, Term> = {
  contact: { label: "Contact", description: "The lead was reached." },
  no_contact: { label: "No contact", description: "The lead was not reached." },
  success: { label: "Success", description: "A win: signed, or an appointment set." },
  compliance: {
    label: "Compliance",
    description: "Marks the lead do-not-call, whatever the kind says.",
  },
};

// --- the window's groups (ADR-160) ------------------------------------------------------------

/** `OUTCOME_GROUPS` in `platform`: where a rep finds an outcome after a call, in the window's order,
 *  and the colour it carries wherever it is shown (`OutcomeDot` in `components/ui/outcome.tsx`). */
export const OUTCOME_GROUPS = [
  "signed",
  "booked",
  "working",
  "not_reached",
  "not_qualified",
  "lost",
  "dnc",
] as const;
export type OutcomeGroup = (typeof OUTCOME_GROUPS)[number];

const GROUP: Record<OutcomeGroup, Term> = {
  signed: { label: "Signed", description: "A win: the client signed, or came back to sign." },
  booked: {
    label: "Booked and callbacks",
    description: "A time is set: a callback, an appointment or a contract review.",
  },
  working: { label: "Still working", description: "Reached and still deciding: the calls go on." },
  not_reached: {
    label: "Didn't get through",
    description: "Nobody was reached, or nobody who could talk: voicemail, no answer, a wrong number.",
  },
  not_qualified: { label: "Not qualified", description: "Reached, and the case does not qualify." },
  lost: { label: "Lost and nurture", description: "Gone quiet: MIA, or kept warm for later." },
  dnc: { label: "Do not call", description: "Never called again." },
};

export function isOutcomeGroup(group: string): group is OutcomeGroup {
  return (OUTCOME_GROUPS as readonly string[]).includes(group);
}

/** A group's words. One this console does not know reads as Still working, as it is laid out. */
export function groupTerm(group: string): Term {
  return isOutcomeGroup(group) ? GROUP[group] : GROUP.working;
}

/** The group an outcome is laid out in: its own, or Still working for one this console does not know. */
export function outcomeGroup(d: Pick<Disposition, "outcome_group">): OutcomeGroup {
  return isOutcomeGroup(d.outcome_group) ? d.outcome_group : "working";
}

/** The group of a recorded outcome's slug, by the catalog the screen reads; null for a slug that
 *  catalog does not hold (one retired, or another tenant's), which is shown without a colour. */
/** The outcome a recorded slug names: from the first list that holds it. The list in use first,
 *  then every outcome the tenant has had (`useOutcomeHistory`), so a switched-off outcome on an old
 *  record keeps its words and colour (ACL-574). */
export function recordedOutcome<T extends Pick<Disposition, "slug">>(
  slug: string | null | undefined,
  ...lists: (readonly T[] | undefined)[]
): T | undefined {
  if (!slug) return undefined;
  for (const list of lists) {
    const found = list?.find((o) => o.slug === slug);
    if (found) return found;
  }
  return undefined;
}

export function slugGroup(
  catalog: readonly Pick<Disposition, "slug" | "outcome_group">[] | undefined,
  slug: string | null | undefined,
): OutcomeGroup | null {
  const d = slug ? catalog?.find((o) => o.slug === slug) : undefined;
  return d ? outcomeGroup(d) : null;
}

/** `default_outcome_group` in `platform`: where an outcome starts when nobody names a group, so the
 *  form can say it before the save. */
export function defaultOutcomeGroup(d: {
  slug: string;
  kind: string;
  category: string | null;
  stage: string | null;
}): OutcomeGroup {
  if (d.category === "compliance") return "dnc";
  if (d.stage === "not_qualified" && d.category !== "no_contact") return "not_qualified";
  if ((d.category === "success" && d.kind === "terminal") || d.stage === "signed_pending_intake" || d.stage === "signed")
    return "signed";
  if (d.stage === "mia" || d.stage === "long_nurture" || d.stage === "dead" || d.stage === "lost") return "lost";
  if (d.category === "no_contact" || d.stage === "no_response" || d.slug === "dropped") return "not_reached";
  if (d.kind === "callback" || d.kind === "hold") return "booked";
  return "working";
}

/** Words a rep types that no label holds: each stands for the word after it (ADR-160). */
const SEARCH_WORDS: Record<string, string> = {
  vm: "voicemail",
  lawyer: "attorney",
  cb: "callback",
  appt: "appointment",
  nq: "not qualified",
  dnc: "do not call",
  sol: "statute",
};

/** The reasons a lead is not qualified: one Not qualified stands for all of them (ACL-574), so a rep
 *  who types the reason still finds it. */
const NOT_QUALIFIED_REASONS =
  "statute of limitations at fault attorney already settled property damage out of state minor refused rescinded not interested";

/** Whether an outcome answers what a rep typed: every word found in its label, its slug or its
 *  group's name, a word from `SEARCH_WORDS` standing for the word it means as well. */
export function outcomeMatches(
  d: Pick<Disposition, "label" | "slug" | "outcome_group">,
  query: string,
): boolean {
  const reasons = d.outcome_group === "not_qualified" ? ` ${NOT_QUALIFIED_REASONS}` : "";
  const hay = `${d.label} ${d.slug.replace(/_/g, " ")} ${groupTerm(d.outcome_group).label}${reasons}`.toLowerCase();
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  return words.length > 0 && words.every((w) => hay.includes(w) || (SEARCH_WORDS[w] !== undefined && hay.includes(SEARCH_WORDS[w])));
}

/**
 * The labels as a group shows them: a lead-in two or more of the group share ("NQ — ") is said by
 * the group already, so "NQ — at fault" reads "At fault" under Not qualified. Keyed by id; a label
 * with no shared lead-in is itself.
 */
export function shortLabels(group: readonly Pick<Disposition, "id" | "label">[]): Map<number, string> {
  const leadIn = (label: string) => /^(.{1,24}?) — (.+)$/.exec(label);
  const counts = new Map<string, number>();
  for (const d of group) {
    const m = leadIn(d.label);
    if (m) counts.set(m[1]!, (counts.get(m[1]!) ?? 0) + 1);
  }
  return new Map(
    group.map((d) => {
      const m = leadIn(d.label);
      if (!m || (counts.get(m[1]!) ?? 0) < 2) return [d.id, d.label];
      const rest = m[2]!;
      return [d.id, rest.charAt(0).toUpperCase() + rest.slice(1)];
    }),
  );
}

/** A dialer call shorter than this most likely reached nobody who could talk. */
export const SHORT_CALL_SECONDS = 20;

/**
 * The window's suggested row: the outcomes the rep picks most on this campaign (`GET
 * /acd/me/outcomes`), among the ones offered, at most `n`. After a short dialer call the ones that
 * say nobody was reached come first. Empty for a rep with no picks yet: nothing is guessed.
 */
export function suggestedOutcomes<D extends Pick<Disposition, "slug" | "outcome_group">>(
  offered: readonly D[],
  mine: readonly { slug: string; count: number }[],
  { nobodyFirst = false, n = 4 }: { nobodyFirst?: boolean; n?: number } = {},
): D[] {
  const ranked = mine.map((m) => offered.find((d) => d.slug === m.slug)).filter((d): d is D => d !== undefined);
  const first = nobodyFirst ? ranked.filter((d) => outcomeGroup(d) === "not_reached") : [];
  return [...first, ...ranked.filter((d) => !first.includes(d))].slice(0, n);
}

/** A category's words, or null for none. One this console does not know is shown as itself. */
export function categoryTerm(category: string | null): Term | null {
  if (!category) return null;
  return CATEGORY[category] ?? { label: category, description: "Not a category this console knows." };
}

/**
 * Whether choosing it ends the lead as do-not-call rather than as its kind says. The dialer checks
 * this before the kind (`after_disposition`): the slug `dnc`, or anything in the compliance category.
 * A compliance outcome on a call with a lead also marks the lead itself do-not-call, beyond this
 * campaign (`POST /acd/calls/{id}/disposition` in core-api).
 */
export function marksDoNotCall(d: Pick<Disposition, "slug" | "category">): boolean {
  return d.slug === "dnc" || d.category === "compliance";
}

/** The outcomes the dialer writes on a lead itself, for a call no rep was on (ADR-108 in
 *  `platform`): a ring nobody answered, a busy line, a machine it hung up on. */
const WRITTEN_BY_THE_DIALER: ReadonlySet<string> = new Set(["no_answer", "busy", "voicemail"]);

/**
 * What to say under an outcome's stage when the stage may wait for a call's redials (ADR-108 in
 * `platform`). A call's redials are the same call again, so the outcome is written at each dial
 * and the stage moves after the last one. That holds for the outcomes the dialer writes itself,
 * and for one a rep sets that the dialer may follow with a redial: a retry that says nobody was
 * reached and names no wait of its own (`outcome_requests.keep_back` in core-api). Null for any
 * other outcome, whose stage moves when it is set, and for one that keeps the stage.
 */
export function stageWaitsNote(
  d: Pick<DispositionValues, "slug" | "kind" | "category" | "backoff_minutes"> & { stage: string | null },
): string | null {
  if (!d.stage) return null;
  const followedByARedial = d.kind === "retry" && d.category === "no_contact" && !d.backoff_minutes.trim();
  if (!WRITTEN_BY_THE_DIALER.has(d.slug) && !followedByARedial) return null;
  return "On a dialer call with redials left, the stage moves after the last redial.";
}

// --- the icon beside an outcome ----------------------------------------------------------------

/** An icon and the words it stands for, which a hover shows. */
export interface KindMark {
  icon: LucideIcon;
  label: string;
}

/** A retry is the ordinary outcome and has none, so the ones that do something else stand out. */
const KIND_ICON: Record<string, LucideIcon> = {
  terminal: PhoneOff,
  callback: AlarmClock,
  hold: Hourglass,
};

/** A kind's icon, or null for a retry or a kind this console does not know. */
export function kindMark(kind: string): KindMark | null {
  const icon = KIND_ICON[kind];
  return icon ? { icon, label: dispositionKindText(kind) } : null;
}

const DO_NOT_CALL_MARK: KindMark = { icon: Ban, label: "Do not call" };
const KEEPS_CALLING_MARK: KindMark = { icon: PhoneOutgoing, label: "Keeps being called" };

/**
 * Whether the campaign goes on calling a lead this outcome puts in an end stage: it gives that
 * stage a cadence of its own (ADR-087 in platform), so MIA on a one-campaign floor is a client
 * to reach again, not a lead that is done. `stages` are the stages the campaign has a cadence
 * for, where the reader may see them (a supervisor); the server's own word on the outcome
 * (`works_stage`) wins when it is sent. Never a do-not-call.
 */
export function keepsCalling(
  d: Pick<Disposition, "slug" | "category"> & Partial<Pick<Disposition, "stage" | "works_stage">>,
  stages: ReadonlySet<string> = new Set(),
): boolean {
  if (marksDoNotCall(d) || !d.stage || d.stage === "dnc") return false;
  return d.works_stage ?? stages.has(d.stage);
}

/** The icon an outcome shows, read the way the dialer reads it: do-not-call before the kind. A
 *  terminal outcome whose stage the campaign keeps working (`keepsCalling`) says so. */
export function outcomeMark(
  d: Pick<Disposition, "slug" | "kind" | "category">,
  kept = false,
): KindMark | null {
  if (marksDoNotCall(d)) return DO_NOT_CALL_MARK;
  return kept && d.kind === "terminal" ? KEEPS_CALLING_MARK : kindMark(d.kind);
}

// --- when the lead is called next --------------------------------------------------------------

/** `DEFAULT_CALLBACK_SECONDS` and `DEFAULT_HOLD_SECONDS` in `recycling.py`. */
export const DEFAULT_CALLBACK_SECONDS = 3_600;
/** The dialer's own wait after a dropped call before it calls the lead for the rep (ADR-085). */
export const DROPPED_WAIT_SECONDS = 900;
export const DEFAULT_HOLD_SECONDS = 86_400;

export interface NextCall {
  text: string;
  /** No span of the disposition's own decides it: a default does. */
  fallback: boolean;
}

/**
 * When the dialer calls the lead again, read the way `after_disposition` reads it: never after a
 * do-not-call outcome or a terminal one; only on a cadence for the stage after a terminal one that
 * puts the lead in MIA, the one end stage a campaign can give a cadence (ADR-087 in platform), since
 * this catalog does not say which campaigns do; after a callback's backoff, or an hour; after a hold's hold, else
 * its backoff, else a day; after a retry's backoff, or the cadence's next wait (ADR-067). The dialer
 * reads each span as `seconds or default`, so a zero is unset here too.
 */
export function nextCall(
  d: Pick<Disposition, "slug" | "kind" | "category" | "backoff_seconds" | "hold_seconds"> &
    Partial<Pick<Disposition, "stage">>,
  stageLabel: string | null = null,
): NextCall {
  if (marksDoNotCall(d)) return { text: "Never", fallback: false };
  if (d.kind === "terminal") {
    return stageLabel && d.stage === RECOVERY_STAGE
      ? { text: `Only on a ${stageLabel} cadence`, fallback: false }
      : { text: "Never", fallback: false };
  }
  if (d.slug === "dropped") {
    // Kept for the rep to call back by hand; the dialer's own wait when the outcome names none.
    return d.backoff_seconds
      ? { text: `After ${formatSpan(d.backoff_seconds)}, for the rep`, fallback: false }
      : { text: `After ${formatSpan(DROPPED_WAIT_SECONDS)} (default), for the rep`, fallback: true };
  }
  if (d.kind === "callback") {
    return d.backoff_seconds
      ? { text: `After ${formatSpan(d.backoff_seconds)}`, fallback: false }
      : { text: `After ${formatSpan(DEFAULT_CALLBACK_SECONDS)} (default)`, fallback: true };
  }
  if (d.kind === "hold") {
    const own = d.hold_seconds || d.backoff_seconds;
    return own
      ? { text: `After ${formatSpan(own)}`, fallback: false }
      : { text: `After ${formatSpan(DEFAULT_HOLD_SECONDS)} (default)`, fallback: true };
  }
  return d.backoff_seconds
    ? { text: `After ${formatSpan(d.backoff_seconds)}`, fallback: false }
    : { text: "After the cadence's next wait", fallback: true };
}

/** The span a kind waits on: a callback and a retry their backoff, a hold its hold, a terminal
 *  outcome none. Any other kind is a retry to the dialer. A hold falls back on its backoff, which
 *  the form does not offer: one span per kind is what a person setting it up means. */
export function spanField(kind: string): "backoff" | "hold" | null {
  if (kind === "terminal") return null;
  return kind === "hold" ? "hold" : "backoff";
}

/** What an empty span field leaves the wait to, for the hint under it. */
export function spanDefaultText(kind: string, backoffSeconds: number | null): string {
  if (kind === "callback") return `Empty calls back after ${formatSpan(DEFAULT_CALLBACK_SECONDS)}.`;
  if (kind === "hold") {
    return backoffSeconds
      ? `Empty holds for its backoff, ${formatSpan(backoffSeconds)}.`
      : `Empty holds for ${formatSpan(DEFAULT_HOLD_SECONDS)}.`;
  }
  return "Empty waits the cadence's next wait.";
}

// --- the form ----------------------------------------------------------------------------------

/**
 * Whole minutes, from one to a year. The server takes any number of seconds from 0, but a zero is
 * unset to the dialer — an empty field says that — and the column is a 32-bit count of seconds,
 * which no sensible wait needs past a year.
 */
export const SPAN_MINUTES = [1, 365 * 24 * 60] as const;

/** Why a span field cannot be saved, or null when it can. Empty is the default, and fine. */
export function minutesProblem(text: string): string | null {
  const t = text.trim();
  if (t === "") return null;
  if (!/^\d+$/.test(t)) return "Whole minutes, or empty for the default";
  const minutes = Number(t);
  if (minutes < SPAN_MINUTES[0]) return "At least 1 minute, or empty for the default";
  if (minutes > SPAN_MINUTES[1]) return `At most ${SPAN_MINUTES[1].toLocaleString()} minutes, a year`;
  return null;
}

/** A span field as seconds, or null when it is empty. Only for a field `minutesProblem` passes. */
export function minutesToSeconds(text: string): number | null {
  const t = text.trim();
  return t === "" ? null : Number(t) * 60;
}

/** The catalog's form. A span is whole minutes as typed, `""` for its default; no category is `""`. */
export interface DispositionValues {
  slug: string;
  label: string;
  kind: string;
  category: string;
  backoff_minutes: string;
  hold_minutes: string;
  /** The campaign this outcome hands the lead to; "" for none (ADR-056). */
  enlist_campaign_id: string;
  requires_note: boolean;
  /** The stage the outcome puts the lead in (ADR-046); `""` keeps it. */
  stage: string;
  /** The window's group (ADR-160); `""` on a new outcome lets its kind, category and stage say. */
  outcome_group: string;
  /** Offered to reps; off for an outcome the platform records by itself. */
  rep_can_pick: boolean;
  /** Missed bookings before `no_show_outcome` is set (ADR-181); "" counts nothing. */
  no_show_limit: string;
  no_show_outcome: string;
}

export const NEW_DISPOSITION: DispositionValues = {
  slug: "",
  label: "",
  kind: "retry",
  category: "",
  backoff_minutes: "",
  hold_minutes: "",
  enlist_campaign_id: "",
  requires_note: false,
  stage: "",
  outcome_group: "",
  rep_can_pick: true,
  no_show_limit: "",
  no_show_outcome: "",
};

export function dispositionValues(d: Disposition): DispositionValues {
  return {
    slug: d.slug,
    label: d.label,
    kind: d.kind,
    category: d.category ?? "",
    backoff_minutes: minutesText(d.backoff_seconds),
    hold_minutes: minutesText(d.hold_seconds),
    enlist_campaign_id: d.enlist_campaign_id ?? "",
    requires_note: d.requires_note,
    stage: d.stage ?? "",
    outcome_group: d.outcome_group,
    rep_can_pick: d.rep_can_pick,
    no_show_limit: d.no_show_limit ? String(d.no_show_limit) : "",
    no_show_outcome: d.no_show_outcome ?? "",
  };
}

/** Seconds as the form's minutes, rounded — `dispositionChanges` keeps the rounding off the wire —
 *  and `""` for unset, which a zero is to the dialer. */
function minutesText(seconds: number | null): string {
  return seconds ? String(Math.max(1, Math.round(seconds / 60))) : "";
}

/** What `POST /dispositions` takes: trimmed, an empty category or span as null, only the span the
 *  kind waits on, and the place in the list it goes. */
export function newDispositionPayload(
  values: DispositionValues,
  sortOrder: number,
): Required<Omit<DispositionInput, "tenant_id">> {
  const span = spanField(values.kind);
  return {
    slug: values.slug.trim(),
    label: values.label.trim(),
    kind: values.kind,
    category: values.category || null,
    backoff_seconds: span === "backoff" ? minutesToSeconds(values.backoff_minutes) : null,
    hold_seconds: span === "hold" ? minutesToSeconds(values.hold_minutes) : null,
    enlist_campaign_id: values.enlist_campaign_id || null,
    requires_note: values.requires_note,
    sort_order: sortOrder,
    stage: values.stage || null,
    outcome_group: values.outcome_group || null,
    rep_can_pick: values.rep_can_pick,
    ...noShowLimit(values),
  };
}

/** The no-show limit the form says (ADR-181): a number and an outcome together, on an outcome that
 *  books a time, else none. */
export function noShowLimit(
  values: Pick<DispositionValues, "kind" | "no_show_limit" | "no_show_outcome">,
): { no_show_limit: number | null; no_show_outcome: string | null } {
  const n = Number(values.no_show_limit);
  if (values.kind !== "callback" || !values.no_show_outcome || !Number.isInteger(n) || n < 1) {
    return { no_show_limit: null, no_show_outcome: null };
  }
  return { no_show_limit: n, no_show_outcome: values.no_show_outcome };
}

/**
 * The fields an edit changed, as `PUT /dispositions/{id}` takes them, and nothing else. A span the
 * form shows rounded to whole minutes goes out only when somebody changed it — 90 s shows as 2
 * minutes, and must not come back as 120 s because the label was fixed — and a span the kind does
 * not wait on is left as it is, not cleared behind the editor's back.
 */
export function dispositionChanges(values: DispositionValues, d: Disposition): DispositionUpdateInput {
  const before = dispositionValues(d);
  const span = spanField(values.kind);
  const out: DispositionUpdateInput = {};
  const label = values.label.trim();
  if (label !== d.label) out.label = label;
  if (values.kind !== d.kind) out.kind = values.kind;
  if (values.category !== before.category) out.category = values.category || null;
  if (span === "backoff" && values.backoff_minutes.trim() !== before.backoff_minutes) {
    out.backoff_seconds = minutesToSeconds(values.backoff_minutes);
  }
  if (span === "hold" && values.hold_minutes.trim() !== before.hold_minutes) {
    out.hold_seconds = minutesToSeconds(values.hold_minutes);
  }
  if (values.enlist_campaign_id !== before.enlist_campaign_id) {
    out.enlist_campaign_id = values.enlist_campaign_id || null;
  }
  if (values.requires_note !== d.requires_note) out.requires_note = values.requires_note;
  if (values.stage !== before.stage) out.stage = values.stage || null;
  if (values.outcome_group && values.outcome_group !== d.outcome_group) out.outcome_group = values.outcome_group;
  if (values.rep_can_pick !== d.rep_can_pick) out.rep_can_pick = values.rep_can_pick;
  const limit = noShowLimit(values);
  if (limit.no_show_limit !== (d.no_show_limit ?? null) || limit.no_show_outcome !== (d.no_show_outcome ?? null)) {
    Object.assign(out, limit);
  }
  return out;
}

// --- the order ---------------------------------------------------------------------------------

/** The gap the catalog is seeded with (`ensure_tenant_defaults`), and the one an order is saved in. */
export const SORT_STEP = 10;

/** Where a new disposition goes: after the last. The server's own default is 100, which in a seeded
 *  catalog is the tenth place. */
export function nextSortOrder(rows: readonly Pick<Disposition, "sort_order">[]): number {
  return rows.reduce((max, d) => Math.max(max, d.sort_order), 0) + SORT_STEP;
}

/** An order being edited, kept in step with the catalog under it: the ids still there, in the
 *  edited order, then any it has gained since, in catalog order. */
export function reconcileOrder(draft: readonly number[], rows: readonly Pick<Disposition, "id">[]): number[] {
  const present = new Set(rows.map((d) => d.id));
  const kept = draft.filter((id) => present.has(id));
  const seen = new Set(kept);
  return [...kept, ...rows.map((d) => d.id).filter((id) => !seen.has(id))];
}

/**
 * The writes that put the catalog in `ids` order: every row renumbered in tens, as the seed numbers
 * it, and a write only for a row whose number moves. The whole list is renumbered rather than two
 * rows swapped, because rows can share a `sort_order` — every one created with the server's default
 * is 100 — and the id breaks that tie, so swapping two equal numbers would change nothing.
 */
export function orderChanges(
  rows: readonly Pick<Disposition, "id" | "sort_order">[],
  ids: readonly number[],
): { id: number; sort_order: number }[] {
  const current = new Map(rows.map((d) => [d.id, d.sort_order]));
  const out: { id: number; sort_order: number }[] = [];
  ids.forEach((id, i) => {
    const sortOrder = (i + 1) * SORT_STEP;
    const was = current.get(id);
    if (was !== undefined && was !== sortOrder) out.push({ id, sort_order: sortOrder });
  });
  return out;
}

/**
 * Whose catalog the page shows. Below superadmin, the reader's own tenant, which core-api reads off
 * them whatever is asked. A superadmin names one — the tenant asked for in the address, else the
 * first — and with no tenant at all there is no catalog to show.
 */
export function catalogTenant(
  actor: { role: Role; tenant_id: number | null } | null,
  tenants: readonly Pick<Tenant, "id">[] | undefined,
  requested: number | null,
): number | null {
  if (!actor) return null;
  if (actor.role !== "superadmin") return actor.tenant_id;
  const first = tenants?.[0];
  if (!first) return null;
  return tenants.some((t) => t.id === requested) ? requested : first.id;
}
